import { useBeneficiariesQuery } from "hooks/queries/useBeneficiariesQuery"
import { BeneficiaryDetails, BeneficiaryEntityType, BeneficiaryTransferDestination, BeneficiaryType } from "types/beneficiaries"
import { BankAccount, Currencies } from "types/types"
import { useGetPaymentMethods } from "../queries/Beneficiaries/useGetPaymentMethods"
import { useEffect, useState } from "react"
import { getAccountNumber, getBankCode } from "utils/bankAccount"

interface UseGetFiatBeneficiariesProps {
  beneficiaryType: BeneficiaryType
  currency: string
}

const mapPaymentMethodToBenficiaryDetails = (paymentMethod: BankAccount) => {
  return {
    currency: paymentMethod?.currency,
    beneficiaryType: BeneficiaryType.FIRST_PARTY,
    businessName: paymentMethod?.description,
    entityType: BeneficiaryEntityType.BUSINESS,
    fiat: {
      accountNumber: getAccountNumber(paymentMethod),
      code: getBankCode(paymentMethod),
      bankName: paymentMethod?.options?.bankName,
      bankAddress: paymentMethod?.options?.beneficiaryBank?.address,
      accountType: paymentMethod?.options?.accountType
    },
    reference: paymentMethod?.id?.toString(),
    transferDestination: BeneficiaryTransferDestination.LOCAL
  } as BeneficiaryDetails
}

export const useGetFiatBeneficiaries = ({ beneficiaryType, currency }: UseGetFiatBeneficiariesProps) => {
  const [fiatBeneficiaries, setFiatBeneficiaries] = useState<BeneficiaryDetails[]>([])
  const { data: paymentMethods, isFetching: isFetchingPaymentMethods, isError: isFetchingPaymentMethodsError, refetch: refetchPaymentMethods } = useGetPaymentMethods({ enabled: false })

  const {
    data: { content: beneficiaries } = {},
    isFetching: isFetchingBeneficiaries,
    isError: isFetchingBeneficiariesError,
    refetch: refetchBeneficiaries
  } = useBeneficiariesQuery(
    {
      sort: 'businessName',
      size: 1000,
      beneficiaryType,
      currency: currency as Currencies,
    },
    false
  )

  useEffect(() => {
    if (currency && beneficiaryType) {
      refetchBeneficiaries()

      if (beneficiaryType === BeneficiaryType.FIRST_PARTY) {
        refetchPaymentMethods()
      }
    }
  }, [beneficiaryType, currency])


  useEffect(() => {
    if (beneficiaryType === BeneficiaryType.FIRST_PARTY && !!paymentMethods?.length && !!beneficiaries?.length && !isFetchingPaymentMethods && !isFetchingBeneficiaries) {
      const mappedBeneficiaries = paymentMethods?.filter(paymentMethod => paymentMethod?.currency === currency)?.map(paymentMethod => {
        const matchingBeneficiary = beneficiaries?.find(
          beneficiary => beneficiary?.reference === paymentMethod?.reference
        )

        if (!matchingBeneficiary) {
          return mapPaymentMethodToBenficiaryDetails(paymentMethod)
        }

        return matchingBeneficiary
      })
      setFiatBeneficiaries(mappedBeneficiaries)
    }
    else if (beneficiaries?.length && !isFetchingBeneficiaries) {
      setFiatBeneficiaries(beneficiaries)
      return
    }

  }, [beneficiaryType, paymentMethods, beneficiaries, isFetchingBeneficiaries, isFetchingPaymentMethods])


  return {
    fiatBeneficiaries,
    isFetching: isFetchingPaymentMethods || isFetchingBeneficiaries,
    isError: isFetchingPaymentMethodsError ?? isFetchingBeneficiariesError
  }
}